import { useState } from 'react';
import type { Recommendation } from '@sdd-maturity/shared-types';
import { compareRecommendations } from '../lib/recommendations';
import { formatDimension } from '../lib/rubrics';
import { Button } from './Button';
import { RecommendationCard } from './RecommendationCard';

interface RecommendationsListProps {
  recommendations: Recommendation[];
}

/** Ordered list of recommendations with effort/impact badges (AC-9.3) */
const MAX_VISIBLE = 6;

export function RecommendationsList({ recommendations }: RecommendationsListProps) {
  const [showAll, setShowAll] = useState(false);

  if (recommendations.length === 0) {
    return (
      <div data-testid="recommendations-list">
        <h3 className="text-lg font-semibold text-primary mb-md">
          Recommendations
        </h3>
        <p className="text-secondary">No recommendations available.</p>
      </div>
    );
  }

  // Deduplicate by title (keep highest priority)
  const seen = new Map<string, Recommendation>();
  for (const r of recommendations) {
    const key = r.title.toLowerCase().slice(0, 50);
    const existing = seen.get(key);
    if (!existing || r.priority < existing.priority) {
      seen.set(key, r);
    }
  }
  const deduped = [...seen.values()];

  // Sort by effort-weighted desirability (high impact + low effort first),
  // tie-broken by numeric priority. See lib/recommendations.ts.
  const sorted = deduped.sort(compareRecommendations);
  const visible = showAll ? sorted : sorted.slice(0, MAX_VISIBLE);

  return (
    <div data-testid="recommendations-list">
      <h3 className="text-lg font-semibold text-primary mb-md">
        Top Recommendations
      </h3>

      <div className="card rec-group">
        {visible.map((rec, index) => (
          <RecommendationCard
            key={`${rec.dimension}-${rec.title}-${index}`}
            index={index + 1}
            title={rec.title}
            description={rec.description}
            effort={rec.effort}
            impact={rec.impact}
            tag={formatDimension(rec.dimension)}
          />
        ))}
      </div>

      {sorted.length > MAX_VISIBLE && (
        <Button
          variant="secondary"
          size="S"
          className="mt-md"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? `Show top ${MAX_VISIBLE}` : `Show all ${sorted.length}`}
        </Button>
      )}
    </div>
  );
}
